import { api } from './api';

export interface OrganizationSiteResponse {
  id: number;
  tenantId: string;
  customerId?: string;
  siteName: string;
  siteUrl: string;
  sitemapUrl: string;
  timezone?: string;
  isActive?: boolean;
  mode?: string;
  agencyCode?: string;
  customerName?: string;
  customerEmail?: string;
  siteTier?: string;
  totalProperties?: number;
  properties?: OrganizationPropertyResponse[];
}

export interface OrganizationPropertyResponse {
  id: number;
  siteId: number;
  toolId: string;
  propertyRef: string;
  displayName?: string;
  isVerified?: boolean;
  isActive?: boolean;
}

export interface OrganizationUserSummary {
  id: string;
  email: string;
  name?: string;
  role?: string;
  profile?: string;
}

export interface AgencyUserSummary {
  userId: string;
  email: string;
  name?: string;
}

export interface OrganizationHierarchyResponse {
  id: string;
  name: string;
  slug?: string;
  planTier?: string;
  type?: string;
  managedBy?: 'SELF' | 'AGENCY';
  maxProperties?: number;
  maxUrlsPerProperty?: number;
  sites?: OrganizationSiteResponse[];
  totalSites?: number;
  totalProperties?: number;
  organizationUsers?: OrganizationUserSummary[];
  agencyUsers?: AgencyUserSummary[];
}

export interface UpdateOrganizationRequest {
  name?: string;
  planTier?: string;
  type?: string;
  maxProperties?: number;
  maxUrlsPerProperty?: number;
}

export interface CreateSiteRequest {
  siteName: string;
  siteUrl: string;
  sitemapUrl: string;
  siteTier: string;
  timezone?: string;
  isActive?: boolean;
  mode?: string;
  businessType?: string;
  customerEmail?: string;
  customerName?: string;
  customerId?: string;
  agencyCode?: string;
  agencyTenantId?: string;
}

export interface UpdateSiteRequest {
  name?: string;
  siteName?: string;
  siteUrl?: string;
  sitemapUrl?: string;
  siteTier?: string;
  timezone?: string;
  isActive?: boolean;
  mode?: string;
  businessType?: string;
  customerEmail?: string;
  customerName?: string;
  customerId?: string;
  agencyCode?: string;
  agencyTenantId?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export const organizationService = {
  async getOrganizations(): Promise<OrganizationHierarchyResponse[]> {
    const response = await api.get<ApiResponse<OrganizationHierarchyResponse[]>>('/customer/organizations');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to load organizations');
    }
    return response.data.data;
  },

  async getOrganization(organizationId: string): Promise<OrganizationHierarchyResponse> {
    const response = await api.get<ApiResponse<OrganizationHierarchyResponse>>(`/customer/organizations/${organizationId}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to load organization');
    }
    return response.data.data;
  },

  async updateOrganization(
    organizationId: string,
    request: UpdateOrganizationRequest,
  ): Promise<OrganizationHierarchyResponse> {
    const response = await api.put<ApiResponse<OrganizationHierarchyResponse>>(`/customer/organizations/${organizationId}`, request);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to update organization');
    }
    return response.data.data;
  },

  async deleteOrganization(organizationId: string): Promise<void> {
    const response = await api.delete<ApiResponse<void>>(`/customer/organizations/${organizationId}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to delete organization');
    }
  },

  async createSite(organizationId: string, request: CreateSiteRequest): Promise<OrganizationSiteResponse> {
    const response = await api.post<ApiResponse<OrganizationSiteResponse>>(`/customer/organizations/${organizationId}/sites`, request);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to create site');
    }
    return response.data.data;
  },

  async updateSite(siteId: number, request: UpdateSiteRequest): Promise<OrganizationSiteResponse> {
    const response = await api.put<ApiResponse<OrganizationSiteResponse>>(`/customer/organizations/sites/${siteId}`, request);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to update site');
    }
    return response.data.data;
  },

  async deleteSite(siteId: number): Promise<void> {
    const response = await api.delete<ApiResponse<void>>(`/customer/organizations/sites/${siteId}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to delete site');
    }
  },
};
